import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '@astro/api'
import { useAuth } from '@astro/auth'
import PageLayout from '../components/layout/PageLayout'
import FloatingFabs from '@astro/floating-fabs'
import { useToast } from '../context/ToastContext'
import { useRazorpayCheckout } from '../hooks/useRazorpayCheckout'
import { releaseUnpaidBooking } from '../utils/bookingCheckout'
import { toIsoDate, formatDateLabel, formatTimeLabel } from '../utils/date'
import { resolveRazorpayKey } from '../utils/payments'
import { daysBetween, tierForDaysUntil } from '../components/sections/booking/maharajPricingData'
import { useMaharajTiers } from '../components/sections/booking/useMaharajTiers'
import { toBookingConsultationType } from '../components/sections/astrologerDetails/astrologerDetailsData'
import MaharajBookingHeroSection from '../components/sections/booking/MaharajBookingHeroSection'
import MaharajScheduleSection from '../components/sections/booking/MaharajScheduleSection'
import MaharajSummaryCard from '../components/sections/booking/MaharajSummaryCard'
import OtherAstrologersSection from '../components/sections/booking/OtherAstrologersSection'
import BookingTrustSection from '../components/sections/booking/BookingTrustSection'
import BirthDetailsForm, { isBirthDetailsValid } from '../components/sections/booking/BirthDetailsForm'
import GemstoneConsultationPromo from '../components/sections/booking/GemstoneConsultationPromo'

/** Single-astrologer response may come back bare or wrapped in a status envelope. */
function unwrapAstrologer(response) {
  if (response && typeof response === 'object' && response.data && typeof response.data === 'object') {
    return response.data
  }
  return response ?? null
}

export default function MaharajBookingPage() {
  const { showToast } = useToast()
  const navigate = useNavigate()
  const { status: authStatus, user } = useAuth()
  const { open: openRazorpay } = useRazorpayCheckout()

  // Defaults to today so the time-slot picker has something to fetch on
  // first render instead of sitting on the "select a date" placeholder —
  // today is always bookable (the `zap` tier's minDays is 0).
  const [selectedDate, setSelectedDate] = useState(() => {
    const d = new Date()
    d.setHours(0, 0, 0, 0)
    return d
  })
  const [selectedSlot, setSelectedSlot] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [birthDetails, setBirthDetails] = useState({
    birthDate: '', birthTime: '', place: '', placeLocation: null, notes: '',
    birthTimeUnknown: false, photo: null, leftPalmPhoto: null, rightPalmPhoto: null,
  })

  const today = useMemo(() => {
    const d = new Date()
    d.setHours(0, 0, 0, 0)
    return d
  }, [])

  // This page is a dedicated persona flow, not a live astrologer-by-id route,
  // so the real backend record is resolved by its known slug — bookings/
  // payments only go through the API when a match is found; otherwise we
  // fall back to a simulated confirmation rather than breaking the page.
  // NOTE: `/astrologers/filter?search=` is currently broken upstream (returns
  // 0 results for any term, verified against the live API) — resolve by
  // slug instead of relying on that search.
  const { data: astrologerData } = useQuery({
    queryKey: ['astrologer', 'astrologer-raju-maharaj'],
    queryFn: () => api.astrologers.get('astrologer-raju-maharaj'),
    staleTime: 5 * 60 * 1000,
    retry: false,
  })
  const resolvedAstrologer = useMemo(() => unwrapAstrologer(astrologerData), [astrologerData])
  // The one type this booking is created with — the summary card shows the same value.
  const consultationType = toBookingConsultationType(
    (resolvedAstrologer?.consultation_mode || 'video').split(',')[0].trim(),
  )

  // Live slab prices/windows (GET /astrologer/pricing-slabs?astrologer_id=…,
  // static fallback). `tier` derives from this same list, so the cards, the
  // calendar, the fee and the paid amount always agree.
  const tiers = useMaharajTiers(resolvedAstrologer?.id)
  const daysUntil = selectedDate ? daysBetween(today, selectedDate) : null
  const tier = daysUntil !== null ? tierForDaysUntil(daysUntil, tiers) : null

  // Live-configured gateway key wins over any local VITE_RAZORPAY_KEY —
  // this endpoint is what actually knows which gateway is active in prod.
  const { data: gatewaysData } = useQuery({
    queryKey: ['payment-gateways'],
    queryFn: () => api.payments.gateways(),
    staleTime: 10 * 60 * 1000,
  })
  const razorpayKey = resolveRazorpayKey(gatewaysData)

  const handleProceed = async () => {
    if (!selectedDate || !tier || isSubmitting) return
    const dateLabel = formatDateLabel(selectedDate)

    if (!isBirthDetailsValid(birthDetails)) {
      showToast('Please fill in your date, time and place of birth to continue.')
      return
    }

    if (authStatus !== 'authenticated') {
      showToast('Please sign in to continue your booking.')
      navigate(`/login?redirect=${encodeURIComponent('/book-maharaj-ji')}`)
      return
    }

    if (!resolvedAstrologer) {
      showToast(`Redirecting to secure payment · ₹${tier.price.toLocaleString('en-IN')} (${tier.label}) · ${dateLabel}…`)
      return
    }

    if (!selectedSlot) {
      showToast('Please choose a time slot to continue.')
      return
    }
    const time = selectedSlot.start_time ?? selectedSlot.time
    const timeLabel = formatTimeLabel(time)

    setIsSubmitting(true)
    const isoDate = toIsoDate(selectedDate)
    const priceLabel = `₹${tier.price.toLocaleString('en-IN')}`

    const bookingFields = {
      name: user?.name ?? '',
      phone: user?.mobile ?? '',
      email: user?.email ?? '',
      consultation_type: consultationType,
      astrologer_id: resolvedAstrologer.id,
      date: isoDate,
      slot_id: selectedSlot.id,
      payment_method: 'razorpay',
      // The tiered priority price, not the astrologer's own base rate —
      // POST /bookings' optional `rate` passthrough is what the backend
      // stores/shows as the booking's price; omitting it here left the
      // amount unset/wrong on the backend and on the confirmation page.
      rate: tier.price,
      birth_date: birthDetails.birthDate,
      place: birthDetails.place,
      notes: birthDetails.notes,
    }
    // Birth time unknown → a photo + both palms substitute for it, which
    // requires multipart/form-data instead of the usual JSON body.
    const bookingPayload = birthDetails.birthTimeUnknown
      ? Object.entries({
          ...bookingFields,
          photo: birthDetails.photo,
          left_palm_photo: birthDetails.leftPalmPhoto,
          right_palm_photo: birthDetails.rightPalmPhoto,
        }).reduce((form, [key, val]) => {
          form.append(key, val ?? '')
          return form
        }, new FormData())
      : { ...bookingFields, birth_time: birthDetails.birthTime }

    let bookingId = null
    try {
      // Backend requires a booking to exist before an order can be created
      // for it, so this reserves the slot first, then pays.
      const bookingResponse = await api.bookings.create(bookingPayload)
      // Envelope key is inconsistent across this backend's endpoints (the
      // Razorpay verify response nests under `booking`, others under `data`)
      // — try both before falling back to the raw response.
      const booking = bookingResponse?.data ?? bookingResponse?.booking ?? bookingResponse
      bookingId = booking?.id
      if (!bookingId) {
        showToast('Booking was reserved but its id was missing from the response — please contact support.')
        setIsSubmitting(false)
        return
      }
    } catch (err) {
      showToast(err?.error?.message || err?.message || 'Could not reserve this slot. Please try again.')
      setIsSubmitting(false)
      return
    }

    try {
      // Backend converts to paise itself (confirmed live — sending
      // pre-converted paise here doubled the charge to 100x the price).
      const orderResponse = await api.bookings.createRazorpayOrder({
        amount: tier.price,
        currency: 'INR',
        booking_id: bookingId,
      })
      // Same envelope convention as every other endpoint here — the order
      // payload rides under `data`, not the top level.
      const order = orderResponse?.data ?? orderResponse

      const payment = await openRazorpay({
        order,
        keyId: razorpayKey,
        name: 'Raju Maharaj Ji · Priority Audience',
        description: `${tier.label} · ${dateLabel} at ${timeLabel}`,
      })

      // Verify the signature server-side — this is what marks the booking paid/confirmed.
      await api.bookings.verifyRazorpayPayment({
        booking_id: bookingId,
        razorpay_order_id: payment.razorpay_order_id,
        razorpay_payment_id: payment.razorpay_payment_id,
        razorpay_signature: payment.razorpay_signature,
      })

      showToast(`Booking confirmed · ${priceLabel} (${tier.label}) · ${dateLabel}`)
      navigate(`/bookings/${bookingId}`)
    } catch (err) {
      // No verified payment, so nothing was booked: release the reservation
      // rather than leave a phantom booking in the seeker's list holding the
      // slot (see releaseUnpaidBooking).
      if (err?.code === 'no_gateway_key') {
        await releaseUnpaidBooking(bookingId, 'Payment gateway unavailable')
        showToast('Payment gateway isn’t configured yet — nothing was booked or charged. Please contact our team.')
      } else if (err?.code === 'cancelled') {
        await releaseUnpaidBooking(bookingId, 'Payment cancelled by customer')
        showToast('Payment cancelled — nothing was booked, and the slot has been released.')
      } else {
        await releaseUnpaidBooking(bookingId, 'Payment failed')
        showToast(err?.error?.message || err?.message || 'Payment could not be completed, so nothing was booked. Please try again.')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <PageLayout className="bg-[#403c3d]/5 text-[#151b21] antialiased min-h-screen flex flex-col">
      <main className="grow">
        <div className="flex flex-col min-h-screen bg-background pb-20">
          <MaharajBookingHeroSection />

          <div className="relative">
            <div aria-hidden="true" className="absolute inset-0 pointer-events-none overflow-hidden">
              <div className="absolute top-32 -right-32 rounded-full" style={{ width: 480, height: 480, background: 'radial-gradient(circle,rgba(245,210,140,.22) 0%,transparent 70%)', filter: 'blur(70px)' }} />
              <div className="absolute bottom-0 -left-24 rounded-full" style={{ width: 420, height: 420, background: 'radial-gradient(circle,rgba(220,185,120,.18) 0%,transparent 65%)', filter: 'blur(80px)' }} />
              <div className="absolute inset-0 opacity-[0.3]" style={{ backgroundImage: 'radial-gradient(circle,rgba(200,146,42,.2) 1px,transparent 1px)', backgroundSize: '40px 40px' }} />
            </div>

            <section className="container px-6 mx-auto mt-10 relative">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
                <div className="lg:col-span-7 space-y-8">
                  <GemstoneConsultationPromo />
                  <MaharajScheduleSection
                    tiers={tiers}
                    astrologerId={resolvedAstrologer?.id}
                    selectedDate={selectedDate}
                    selectedSlot={selectedSlot}
                    activeTierKey={tier?.key ?? null}
                    onSelectDate={(date) => { setSelectedDate(date); setSelectedSlot(null) }}
                    onSelectSlot={setSelectedSlot}
                  />
                  <BirthDetailsForm value={birthDetails} onChange={setBirthDetails} allowUnknownBirthTime />
                </div>
                <aside className="lg:col-span-5 lg:sticky lg:top-28">
                  <MaharajSummaryCard
                    consultationType={consultationType}
                    selectedDate={selectedDate}
                    selectedSlot={selectedSlot}
                    requiresSlot={Boolean(resolvedAstrologer)}
                    tier={tier}
                    daysUntil={daysUntil}
                    birthDetailsValid={isBirthDetailsValid(birthDetails)}
                    onProceed={handleProceed}
                    isSubmitting={isSubmitting}
                  />
                </aside>
              </div>
            </section>

            <OtherAstrologersSection excludeId={resolvedAstrologer?.id} />
          </div>

          <BookingTrustSection />
        </div>
      </main>
      <FloatingFabs />
    </PageLayout>
  )
}
